import React, {
  useEffect, useRef, useState,
} from 'react';
import '@yaireo/tagify/dist/tagify.css';
import { MixedTags } from '@yaireo/tagify/dist/react.tagify';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledBox = styled(Box)`
  border-radius: 5px;
  white-space: nowrap;

  & .tagify {
    height: auto;
    border-radius: 5px;
    --tags-focus-border-color: #D3E2E2;
  }

  & .tagify--mix {
    border: none;
    display: flex;
    font-family: sans-serif;
    font-size: 13px;
    line-height: 15px;
    margin-left: -2px;
    --tag-bg: #d5e5fa;
    --tag-hover: #d5e5fa;
    --tag-border-radius: 20px;
  }

  & .tagify--mix .tagify__input {
    white-space: initial;
    line-height: 2;
  }
`;

interface Props {
  error: boolean;
  whitelist: any[];
  selectedTag: string;
  isReadOnly: boolean;
  currentPattern: string;
  onChange: (input: string) => void;
}

export const replaceLabelsWithValues = (input: string, whitelist: any[]) => {
  let modifiedInput = input;

  whitelist.forEach(({ label, value }) => {
    if (modifiedInput.includes(label)) {
      const regex = new RegExp(label, 'gi');
      modifiedInput = modifiedInput.replace(regex, `{{${value}}}`);
    }
  });

  return modifiedInput;
};

const TextAndTagsEditor = ({
  error = false,
  whitelist = [],
  isReadOnly,
  selectedTag = '',
  currentPattern = '',
  onChange,
}: Props) => {
  const inputRef = useRef<any>(null);
  const [firstInput, setFirstInput] = useState(currentPattern);

  useEffect(() => {
    if (inputRef.current && selectedTag) {
      setFirstInput(currentPattern.replace('\n', ''));
    }
  }, [selectedTag]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.update();
    }
    onChange('');
  }, [firstInput]);

  const settings = {
    whitelist,
    mode: 'mix',
    duplicates: true,
    tagTextProp: 'label',
    pattern: /[@#]/,
    dropdown: {
      enabled: 1,
      includeSelectedTags: true,
      mapValueTo: 'label',
      searchKeys: ['label', 'value'],
      highlightFirst: true, // automatically highlights first sugegstion item in the dropdown
    },
    enforceWhitelist: true,
    mixTagsInterpolator: ['{{', '}}'],
    mixMode: {
      insertAfterTag: encodeURI(''),
    },
    originalInputValueFormat: (val: any) => val.value,
  };

  const handleInput = () => {
    inputRef.current.update();
    const originalInput = inputRef.current?.state?.lastOriginalValueReported;
    onChange(originalInput);
  };

  return (
    <StyledBox
      border={error ? '1px solid #ff3b30' : '1px solid #D3E2E2'}
    >
      <MixedTags
        autofocus
        settings={settings}
        tagifyRef={inputRef}
        readOnly={isReadOnly}
        onChange={handleInput}
        onInput={handleInput}
        onAdd={handleInput}
        onRemove={handleInput}
        name="text-and-tags-editor"
        value={String(firstInput)}
      />
    </StyledBox>
  );
};

export default TextAndTagsEditor;
